import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';

/**
 * Multi-select class recipes.
 *
 * Zero `dark:` variants and zero slots of its own. The closed field, popup and option rows reuse
 * the input, select and checkbox families so later design-system corrections flow through here.
 *
 * ── What this component reuses, and why that is the whole design ───────────────────────────────
 *
 * Ported from the Figma `Multi-select` set (file 5aSkE0U4TeWbuIZDvccHjc, node 1447:135941, read
 * re-read 2026-09-17 — 84 cells over State x Position x isOpen x isDisabled x hasError x Value).
 *
 * The set's own description states the invariant: "Boundaries and layout match Select and Text
 * Input so all three line up when stacked in a form. The open state uses Menu." So the closed field
 * IS the text input's box and the popup IS the select's menu — not lookalikes built to the same
 * numbers, literally the same recipes:
 *
 *   textInputRootRecipe / textInputBoxRecipe / textInputLabelRecipe / textInputHelperRecipe
 *   textInputAffixRecipe          the box, the floating label, the helper line, the two icon slots
 *   selectTriggerRecipe           the full-box <button> and its padding arithmetic
 *   selectCaretRecipe             the caret's colour, on the field's clock
 *   selectCaretGlyphRecipe        the caret's 180° flip, on the list's clock
 *   selectMenuRecipe              the popup panel
 *   selectPositionerRecipe        the stacking context and the z-index
 *
 * That is what makes "must not drift from Select" true by construction rather than by matching
 * values that could later diverge. What is left below is only the parts a multi-select has and a
 * select does not: a value line that can hold pills, and an option row with a checkbox.
 *
 * Focus and open styling are also shared. `textInputBoxRecipe` owns the field treatment and reads
 * `data-open` from the common field root, exactly as `Select` does.
 *
 * ── Where the code follows the drawing, and the one place it does not ──────────────────────────
 *
 * THE BADGES CELL IS DRAWN 1:1, re-measured 2026-09-23: 62px tall (84px with helper text), the
 * floated label 8px from the outer edge, the pills 30–54 and 8px of air under them. An earlier port
 * held the cell to the family's 60px by taking those pixels out of the bottom inset, which left the
 * pills 3px off the border and read as cramped. Two taller pixels are harmless in a `FieldGroup`,
 * which stacks fields vertically and only shares the horizontal seam. See
 * `multiSelectBadgesTriggerRecipe`.
 *
 * THE TEXT DISPLAYS KEEP THE FAMILY'S LINES. The set draws its Text cell's label at 9px and value at
 * 31px where `Select` and `Text Input` draw 10 and 30. The family's numbers stand, so a multi-select
 * showing a count lines up with a select beside it. A one-pixel difference between sibling sets is
 * the drawing's inconsistency rather than an intent.
 */

/**
 * The value line.
 *
 * `count` and `labels` are one line of text and behave exactly as `selectValueRecipe` does, down to
 * the `min-h-[20px]`, which is load-bearing for the same reason: with nothing selected this element
 * renders no text at all, and without a floor it collapses to zero and the trigger measures 38px
 * instead of 58 — the box still looks right because it carries its own `min-h-[60px]`, but the
 * bottom third of an empty field stops opening the menu.
 *
 * `badges` is a flex row instead, so the pills sit on one line (their gap lives on
 * `multiSelectBadgeListRecipe`, which is the frame the set actually gaps). `overflow-hidden` rather
 * than `truncate` because an ellipsis belongs to text: the pills are capped by COUNT before they
 * get here (see `maxBadges`), which is what honours the drawing's rule that the value line never
 * truncates mid-word.
 */
export const multiSelectValueRecipe = cva(['min-h-[20px]'], {
  variants: {
    display: {
      count: 'block truncate',
      labels: 'block truncate',
      badges: 'flex items-center overflow-hidden',
    },
  },
  defaultVariants: { display: 'count' },
});

/**
 * The Badges cell's interior, measured off Figma 1447:139409 and 1447:139921.
 *
 * Offsets from the OUTER edge, 1px border included:
 *
 *                     label     pills      below     height
 *   no helper         8–28      30–54      8         62
 *   helper text       7–27      29–53      helper    84      (helper 57–77, then 7)
 *
 * The 2px between the label's line box and the pills is the set's own `x0,5` gap. The trigger pays
 * the vertical inset, so `pt` is the pill top minus the border. With helper text the bottom inset
 * moves to the box — see `multiSelectBadgesBoxRecipe`.
 *
 * Only applied when pills are actually drawn. An empty badges field IS the Empty cell — unfloated
 * label, 60px — so it keeps the family's geometry.
 *
 * `labelHidden` has no drawn cell. The pill row is centred in the family's 60px (72px with helper)
 * rather than in the 62, since with no label there is no stack to make taller.
 *
 * `cn()` is `tailwind-merge`, so these REPLACE `selectTriggerRecipe`'s `pt-*`/`pb-*` rather than
 * racing them in the stylesheet.
 */
export const multiSelectBadgesTriggerRecipe = cva([], {
  variants: {
    badges: { true: '', false: '' },
    labelHidden: { true: '', false: '' },
    hasHelper: { true: '', false: '' },
  },
  compoundVariants: [
    { badges: true, labelHidden: false, hasHelper: false, class: 'pt-[29px] pb-[7px]' },
    { badges: true, labelHidden: false, hasHelper: true, class: 'pt-[28px] pb-0' },
    { badges: true, labelHidden: true, hasHelper: false, class: 'py-[17px]' },
    { badges: true, labelHidden: true, hasHelper: true, class: 'pt-[16px] pb-0' },
  ],
  defaultVariants: { badges: false, labelHidden: false, hasHelper: false },
});

/**
 * With helper text the family puts 7px under the helper on the BOX; the Badges error cell draws 6.
 */
export const multiSelectBadgesBoxRecipe = cva([], {
  variants: {
    badges: { true: '', false: '' },
    hasHelper: { true: '', false: '' },
  },
  compoundVariants: [{ badges: true, hasHelper: true, class: 'pb-[6px]' }],
  defaultVariants: { badges: false, hasHelper: false },
});

/**
 * The floated label, lifted to where the Badges cell draws it: 8px from the outer edge, 7px with
 * helper text, against the family's 10. Both floated selectors are restated because
 * `textInputLabelRecipe` floats on either, and `tailwind-merge` replaces each by its own modifier.
 * A badges field with pills is always filled, so the resting translate never shows.
 */
export const multiSelectBadgesLabelRecipe = cva([], {
  variants: {
    badges: { true: '', false: '' },
    hasHelper: { true: '', false: '' },
  },
  compoundVariants: [
    {
      badges: true,
      hasHelper: false,
      class: 'group-data-[filled]/ti:translate-y-[7px] group-data-[focused]/ti:translate-y-[7px]',
    },
    {
      badges: true,
      hasHelper: true,
      class: 'group-data-[filled]/ti:translate-y-[6px] group-data-[focused]/ti:translate-y-[6px]',
    },
  ],
  defaultVariants: { badges: false, hasHelper: false },
});

/**
 * The caret, kept centred on the label-and-pills stack as the drawing centres it (y=23 in the 62px
 * cell). With helper text the stack's centre is back where the family's caret already sits, and
 * with the label hidden the pills are centred in the family's box, so only the 62px cell and the
 * label-hidden helper cell move.
 */
export const multiSelectBadgesCaretRecipe = cva([], {
  variants: {
    badges: { true: '', false: '' },
    labelHidden: { true: '', false: '' },
    hasHelper: { true: '', false: '' },
  },
  compoundVariants: [
    { badges: true, labelHidden: false, hasHelper: false, class: 'top-[22px]' },
    { badges: true, labelHidden: true, hasHelper: true, class: 'top-[20px]' },
  ],
  defaultVariants: { badges: false, labelHidden: false, hasHelper: false },
});

/**
 * The pills and the remainder inside a `badges` value line — one row, 4px gap, the set's `x1`.
 *
 * The remainder sits INSIDE this row rather than beside it, because that is where the set puts it:
 * `Overflow count` is a child of the `Badges` frame, not a sibling.
 *
 * ── The 60% dimming is the LIBRARY's answer, not a shortcut ────────────────────────────────────
 *
 * Disabled dims the whole row rather than restyling each pill, and the reason is upstream of this
 * component: **`Tag` has no disabled state, by design.** Re-read on 2026-09-17, the `Tag` set's
 * axes are `State` / `Type` / `Size` only, and its description is explicit — *"colour is identity
 * here, so there is nothing to switch to when a tag sits in a disabled field. Consumers dim the
 * whole tag row to 60% opacity instead — Tag Input and Multi-select both do."*
 *
 * A correction to an earlier draft of this file, which claimed the set's premise was wrong because
 * the SHIPPED `Tag` carries `aria-disabled` styling. The shipped disabled treatment greys the pill,
 * and greying is exactly what the library forbids for a component whose colour IS its meaning — so
 * the code was more "complete" than the design and wrong about it. Passing `disabled` to these
 * pills is what would need justifying, not this.
 *
 * 60% rather than the menu row's disabled foreground: this dims coloured tags without replacing
 * their identity colours. Written as `opacity-60` rather than promoted to a slot because it is one
 * consumer-side value, and because the honest fix is a disabled `Tag` — which the set's description
 * now flags as "worth adding if this keeps recurring". Tag Input will be the second occurrence.
 *
 * `min-w-0` lets the row shrink so the remainder beside the pills stays visible at narrow widths;
 * without it a flex child refuses to go below its content size and the "+3 more" is the first thing
 * pushed out of the box.
 */
export const multiSelectBadgeListRecipe = cva([
  'flex min-w-0 items-center gap-[4px]',
  'group-data-[disabled]/ti:opacity-60',
]);

/**
 * The "+N more" remainder, in `badges` and in `labels`.
 *
 * `text-body-sm` is the set's own binding — the `Overflow count` node carries the `Body/sm` text
 * style (12px Regular on a 20px line), re-read 2026-09-17. It was `text-body-md` here, 14px, which
 * is the value the node carried earlier the same day; at 14px beside a 12px pill the remainder read
 * as a different kind of thing rather than as part of the same row.
 *
 * ONE DIFFERENCE LEFT, and it is the file's rather than a choice made here: the pills' own label is
 * `Label/md` — 12px **Medium** — so the two are the same size and one weight apart. Flagged; if the
 * remainder should match the pills exactly it is `text-label-md`, one word.
 *
 * Colour stays `foreground/primary` via `--ds-input-value`, the same role as the value text, so it
 * reads as part of the value rather than as a caption.
 *
 * `shrink-0` against the pill row's `min-w-0`: the remainder is the count of what is NOT shown, so
 * it is the last thing that should be allowed to disappear.
 */
export const multiSelectOverflowRecipe = cva([
  'shrink-0 whitespace-nowrap',
  'font-sans text-body-sm text-[var(--ds-input-value)]',
  'group-data-[disabled]/ti:text-[var(--ds-input-value-disabled)]',
]);

/**
 * An option row.
 *
 * The same box as `selectItemRecipe` — 14px Medium on a 20px line with 6px of inset — so the two
 * components' menus are one list treatment. The differences are both about the checkbox:
 *
 * THE SELECTED ROW TAKES NO FILL. `Select` shares one fill between its selected and highlighted
 * rows, because with the trailing tick removed the fill is the only thing marking the current
 * option. Here the checkbox marks it, so a selected row keeps its resting background and the fill
 * means one thing only: "this is the row the pointer or the arrow key is on". That matters more than
 * it sounds in a multi-select, where several rows are selected at once and shading them all would
 * leave the highlighted one indistinguishable.
 *
 * THE TEXT RAMP IS `Select`'s, NOT THE SET's. The drawn `List-Item` rows put their label at
 * `foreground/primary` at rest, `foreground/secondary` on HOVER and `foreground/primary` again on
 * focus — hover dims the label, which no other component in the library does and which reads as a
 * cell that was left mid-edit. Reading the resting/hover/selected ramp off `--ds-select-item-*`
 * instead keeps a hovered row brighter than a resting one and keeps the two menus identical.
 * Flagged for design rather than reconciled silently.
 *
 * `data-highlighted` covers BOTH mouse hover and keyboard navigation — Base UI's
 * `highlightItemOnHover` defaults to true — so pointer and keyboard users see the same thing and
 * there is no second state to keep in step.
 *
 * Selectors are mutually exclusive rather than order-dependent, as in the sibling recipes:
 * disabled → selected → highlighted → resting.
 *
 * GEOMETRY is the library's, and it is `selectItemRecipe`'s: a 36px row (8px of inset over a 20px
 * line), 12px across and a 10px corner. The corner was `radius/full` until the 2026-09-22 Menu and
 * List-Item tweak took every row to an unbound 10px; `Select` and `Menu` moved then and this row
 * moves with them, so the three menus stay one list treatment. The test pins that by comparing
 * against both siblings rather than against literals.
 *
 * NO TRANSITION, for the reason `menuItemRecipe` records: `data-highlighted` follows the arrow keys,
 * and a 150ms tint made the highlight trail them. The checkbox inside still eases, because it
 * answers a click rather than a key run.
 */
export const multiSelectItemRecipe = cva([
  'group/msi',
  'relative flex w-full shrink-0 items-center gap-[8px]',
  'm-0 appearance-none border-0 bg-transparent text-left',
  'px-[12px] py-[8px]',
  'rounded-[10px]',
  'font-sans text-label-lg select-none',
  'outline-none',
  'cursor-pointer',

  // Resting, out-specified by everything below.
  'text-[var(--ds-select-item-fg)]',

  // Pointer/keyboard position only — see the note above on why selection does not fill the row.
  'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-select-item-bg-hover)]',

  'data-[highlighted]:not-data-[disabled]:text-[var(--ds-select-item-fg-hover)]',

  focusRing('selectItem'),

  // Reads the slot rather than an `opacity-50` literal, so a stylesheet reader can see the dimming —
  // the same correction `.ds-select` records. It dims the row's own colours rather than recolouring
  // them, which keeps one value right for a resting row and a selected one at once.
  'data-[disabled]:cursor-not-allowed data-[disabled]:text-[var(--ds-select-item-fg-disabled)]',
]);

/**
 * The tick box on an option row.
 *
 * Figma's `List-Item Type=Multi Select` puts a real `Checkbox` instance here, so this reads the
 * `--ds-checkbox-*` slots rather than inventing values — retune the checkbox and these rows follow.
 * The row carries the `ds-checkbox` class for that; the slots it sets are disjoint from
 * `.ds-select`'s, so both can sit on one element.
 *
 * 18px with a 1px INSIDE border and `radius/sm`, which is the rect the library draws (18x18 at
 * (3,3) inside a 24px frame). The 24px hit frame is deliberately NOT reproduced: it exists so a
 * standalone checkbox has a 24px target, and here the whole 36px row is the target. Reproducing it
 * would only add 3px of dead space between the box and the label.
 *
 * No focus ring, and that is the drawing: the `Focus` cells leave their checkbox at `Default`. In a
 * Base UI select the popup holds focus and the rows are never individually focusable, so there is
 * nothing for a ring to answer — `data-highlighted` is the keyboard signal and it is already on the
 * row.
 *
 * Selectors are mutually exclusive on `data-*` alone. Order of the six cases, none overlapping:
 * unchecked resting (base) | unchecked highlighted | selected | selected highlighted |
 * unchecked disabled | selected disabled.
 */
export const multiSelectIndicatorRecipe = cva([
  'relative block size-[18px] shrink-0 rounded-sm',
  'border border-solid',
  'transition-[border-color,background-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

  // Unchecked, resting. The base case — everything below out-specifies it by selector, not by order.
  'border-[var(--ds-checkbox-border)] bg-[var(--ds-checkbox-bg)]',

  'group-[[data-highlighted]:not([data-selected]):not([data-disabled])]/msi:border-[var(--ds-checkbox-border-hover)]',
  'group-[[data-highlighted]:not([data-selected]):not([data-disabled])]/msi:bg-[var(--ds-checkbox-bg-hover)]',

  'group-[[data-selected]:not([data-highlighted]):not([data-disabled])]/msi:border-[var(--ds-checkbox-on-border)]',
  'group-[[data-selected]:not([data-highlighted]):not([data-disabled])]/msi:bg-[var(--ds-checkbox-on-bg)]',

  'group-[[data-selected][data-highlighted]:not([data-disabled])]/msi:border-[var(--ds-checkbox-on-border-hover)]',
  'group-[[data-selected][data-highlighted]:not([data-disabled])]/msi:bg-[var(--ds-checkbox-on-bg-hover)]',

  'group-[:not([data-selected])[data-disabled]]/msi:border-[var(--ds-checkbox-border-disabled)]',
  'group-[:not([data-selected])[data-disabled]]/msi:bg-[var(--ds-checkbox-bg-disabled)]',

  // A disabled ticked box greys all the way out to a neutral rather than washing the blue, so it
  // reads as "switched off" and not as a paler version of on. As drawn, and as `.ds-checkbox` says.
  'group-[[data-selected][data-disabled]]/msi:border-[var(--ds-checkbox-on-border-disabled)]',
  'group-[[data-selected][data-disabled]]/msi:bg-[var(--ds-checkbox-on-bg-disabled)]',
]);

/**
 * The tick itself.
 *
 * Mounted only on a selected row, so there is no hidden state to keep in step. It fades on the box's
 * own clock, as `checkboxGlyphRecipe` does and for the same reason: an instant unmount under a fill
 * that tints over 150ms flashes an empty blue box. `data-ending-style` keeps it mounted until the
 * fade finishes. Sized to the full
 * 18px box with the glyph drawn in Figma's own 24px frame and scaled down by the SVG viewBox, which
 * is how `Checkbox` avoids doing coordinate arithmetic on the path.
 *
 * `pointer-events-none` so it can never intercept the click that belongs to the row.
 */
export const multiSelectIndicatorGlyphRecipe = cva([
  'pointer-events-none absolute inset-[-1px] block',
  'transition-opacity duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
  'text-[var(--ds-checkbox-glyph)]',
  'group-data-[disabled]/msi:text-[var(--ds-checkbox-glyph-disabled)]',
  '[&_svg]:size-full',
]);

/** A per-option leading glyph. Boxed like the field's own affixes so 16px lands the same way in both. */
export const multiSelectItemIconRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  'text-[length:16px] text-[var(--ds-select-item-icon)]',
  'group-data-[disabled]/msi:text-[var(--ds-select-item-fg-disabled)]',
  '[&_svg]:size-full',
]);

/** The option label. Flexes so a trailing cell stays pinned right as the row grows. */
export const multiSelectItemLabelRecipe = cva(['min-w-0 flex-1 truncate']);

/**
 * A per-option trailing cell — the caller's own content, positioned and nothing else.
 *
 * `shrink-0` against the label's `flex-1 truncate`: a long label truncates and the cell keeps its
 * width, rather than the two fighting over the row. No colour, no padding, no background, because
 * this layer has no status vocabulary of its own and the design draws no cell here.
 */
export const multiSelectItemTrailingRecipe = cva(['inline-flex shrink-0 items-center']);
